import React, { useCallback, useMemo } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import CartReviewPriceStack from './CartReviewPriceStack'
import CartReviewBanner from './CartReviewBanner'
import { getBannerTypeVariant } from './CartReviewUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import useUserCartTransferItemData from 'hooks/UserCart/useUserCartTransferItemData'
import LineBagAltIcon from 'LuxKit/Icons/line/LineBagAltIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import CartReviewTileImageContent from './CartReviewTileImageContent'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsSaveForLater, userCartItemsDelete } from 'actions/UserCartActions'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import CartReviewTileContent from './CartReviewTileContent'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import LoadingCartReviewTileSkeleton from './LoadingCartReviewTileSkeleton'
import CondensedCartReviewTile from './CondensedCartReviewTile'
import CondensedCartReviewTileContent from './CondensedCartReviewTileContent'
import CartReviewTileActions from './CartReviewTileActions'

interface Props {
  item: App.Checkout.TransferItem
  tileVariant?: 'default' | 'condensed'
}

function AirportTransferCartReviewTile({ item, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const data = useUserCartTransferItemData(item)

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, data.isSavedForLater)
    dispatch(userCartItemsSaveForLater([item.itemId]))
  }, [dispatch, item, data.isSavedForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete([item.itemId]))
  }, [dispatch, item])
  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(data.itemTotals), [data?.itemTotals])

  const formattedDateRange = useMemo(() => {
    if (!item.transfer.date) return ''
    const formattedDate = moment(item.transfer.date).format(DMY_CASUAL_FORMAT)
    return item.transfer.time ? `${formattedDate} · ${item.transfer.time}` : formattedDate
  }, [item.transfer.date, item.transfer.time])

  const experienceOfferPageUrl = useMemo(() => {
    return getExperienceOfferPageURL(data.experience)
  }, [data.experience])

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, false, !!data.error)
  const cancellationInfo = data.experience?.copy.cancellationInfo
  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    return {
      calculationRequests: [
        generateLoyaltyPointsCalculatorExperienceOptions(data.experience, {}),
      ],
      calculationType: 'exact',
    }
  }, [data.experience])

  const image = data.experience?.images[0]

  if (data.loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (data.isDeleted) {
    return <DeletedCartReviewTile
      title={data.experience?.name || ''}
      itemIds={item.itemId}
    />
  }

  if (tileVariant === 'condensed') {
    return <CondensedCartReviewTile
      to={experienceOfferPageUrl}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CondensedCartReviewTileContent
        title={data.experience?.name || ''}
        dates={formattedDateRange}
      />}
      price={<CartReviewPriceStack
        price={publicTotal}
        memberPrice={memberTotal}
        value={data.itemTotals?.value ?? 0}
        luxPlusOfferConfig={data.experience?.luxPlus}
      />}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
      />}
      onDelete={handleDelete}
    />
  }

  return <CartReviewTile
    to={experienceOfferPageUrl}
    isBookable={data.isBookable}
    saveForLater={data.isSavedForLater}
    imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
    content={<CartReviewTileContent
      title={data.experience?.name || ''}
      dates={formattedDateRange}
      packageTitle={item.transfer.option?.name}
      cancellationPolicy={cancellationInfo && (
        <ExperienceCancellationText cancellationInfo={cancellationInfo} textSize="medium" />
      )}
      metaData={<Group direction="horizontal" gap={12} verticalAlign="center">
        <BodyTextBlock variant="small" colour="neutral-two">
          Max capacity:
        </BodyTextBlock>
        <BodyTextBlock variant="small" colour="neutral-two" startIcon={<LineUsersAltIcon colour="neutral-two" size="S" />}>
          {item.transfer.option?.maxPassengers}
        </BodyTextBlock>
        <BodyTextBlock variant="small" colour="neutral-two" startIcon={<LineBagAltIcon colour="neutral-two" size="S" />}>
          {item.transfer.option?.maxCheckedBags}
        </BodyTextBlock>
      </Group>}
      loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
    />}
    price={<CartReviewPriceStack
      price={publicTotal}
      memberPrice={memberTotal}
      value={data.itemTotals?.value ?? 0}
      luxPlusOfferConfig={data.experience?.luxPlus}
    />}
    banner={<CartReviewBanner
      variant={bannerTypeVariant}
      to={experienceOfferPageUrl}
    />}
    actions={<CartReviewTileActions
      isBookable={data.isBookable}
      saveForLater={data.isSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />}
  />
}

export default AirportTransferCartReviewTile
